import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Card,
  CardContent,
  Container,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material';
import { useClientAuth } from '../contexts/ClientAuthContext';
import { 
  People as PeopleIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import HelpTutorialDialog from '../components/HelpTutorialDialog';
import TermsModal from '../components/legal/TermsModal';
import ComplaintsBookModal from '../components/legal/ComplaintsBookModal';

const ClientLogin = () => {
  const { loginClient, isAuthenticated } = useClientAuth();
  const [view, setView] = useState('login'); // 'login' | 'forgot'
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [complaintsOpen, setComplaintsOpen] = useState(false);
  const navigate = useNavigate();

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const toggleView = (newView) => {
    setView(newView);
    setError('');
    setSuccess('');
    setEmail('');
    setUsername('');
    setPassword('');
    setShowPassword(false);
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!email) {
      setError('Por favor ingrese su correo electrónico');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Por favor ingrese un correo electrónico válido');
      return;
    }

    try {
      setLoading(true);
      console.log(`ClientLogin - Enviando solicitud de recuperación para: ${email}`);

      const response = await fetch('/api/client/auth/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message || 'Si el email está registrado, recibirás instrucciones para restablecer tu contraseña');
        setEmail('');
      } else {
        setError(data.message || 'Error al enviar la solicitud');
      }
    } catch (error) {
      console.error('Error en recuperación:', error);
      setError('Error de conexión. Intente más tarde.');
    } finally {
      setLoading(false);
    }
  };

  const [companySettings, setCompanySettings] = useState({
    company_name: 'YinoWISP',
    company_logo: ''
  });

  // Limpiar tokens de administrador y verificar autenticación + Cargar logo de base de datos
  useEffect(() => {
    // Limpiar token de administrador para evitar conflictos
    localStorage.removeItem('token');
    console.log('ClientLogin - Componente montado, tokens de admin eliminados');

    // Si ya está autenticado, redirigir al dashboard
    if (isAuthenticated) {
      console.log('ClientLogin - Usuario ya autenticado, redirigiendo...');
      navigate('/client/dashboard');
    }

    // Obtener configuraciones de empresa públicas (nombre y logo)
    const fetchCompanySettings = async () => {
      try {
        const response = await fetch('/api/public/company-settings');
        if (response.ok) {
          const data = await response.json();
          setCompanySettings({
            company_name: data.company_name || 'YinoWISP',
            company_logo: data.company_logo || ''
          });
        }
      } catch (error) {
        console.error('Error fetching company settings for login:', error);
      }
    };
    fetchCompanySettings();
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('ClientLogin - Intentando iniciar sesión');
    setError('');

    if (!username || !password) {
      setError('Por favor complete todos los campos');
      return;
    }

    try {
      setLoading(true);
      console.log(`ClientLogin - Enviando solicitud de login: ${username}`);

      const result = await loginClient(username, password);

      if (result && result.error) {
        console.log('ClientLogin - Error de autenticación:', result.error);
        setError(result.error);
      }
    } catch (error) {
      console.error('ClientLogin - Error inesperado:', error);
      setError('Error en el servidor. Por favor intente más tarde.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'radial-gradient(circle at center, #111827 0%, #030712 100%)',
        py: 4,
        px: 2,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          width: '350px',
          height: '350px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(99, 102, 241, 0.12) 0%, rgba(99, 102, 241, 0) 70%)',
          top: '10%',
          left: '15%',
          filter: 'blur(60px)',
          zIndex: 1
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          width: '350px',
          height: '350px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(168, 85, 247, 0.12) 0%, rgba(168, 85, 247, 0) 70%)',
          bottom: '10%',
          right: '15%',
          filter: 'blur(60px)',
          zIndex: 1
        }
      }}
    >
      <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 2 }}>
        <Card
          elevation={0}
          sx={{
            background: 'rgba(17, 24, 39, 0.65)',
            backdropFilter: 'blur(24px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '24px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
            overflow: 'hidden',
            transition: 'border 0.3s ease',
            '&:hover': {
              border: '1px solid rgba(255, 255, 255, 0.12)',
            }
          }}
        >
          <CardContent sx={{ p: { xs: 4, sm: 5 } }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              {companySettings.company_logo ? (
                <Box
                  component="img"
                  src={companySettings.company_logo}
                  alt={companySettings.company_name}
                  sx={{
                    width: 120,
                    height: 120,
                    objectFit: 'contain',
                    mb: 3,
                    mx: 'auto',
                    display: 'block',
                    borderRadius: '24px',
                    filter: 'drop-shadow(0 0 15px rgba(99, 102, 241, 0.4))',
                    '@keyframes premiumIntro': {
                      '0%': { transform: 'scale(0.8) translateY(20px)', opacity: 0, filter: 'drop-shadow(0 0 0px rgba(99, 102, 241, 0))' },
                      '50%': { filter: 'drop-shadow(0 0 25px rgba(168, 85, 247, 0.8))' },
                      '100%': { transform: 'scale(1) translateY(0)', opacity: 1, filter: 'drop-shadow(0 0 15px rgba(99, 102, 241, 0.4))' },
                    },
                    '@keyframes floatAndGlow': {
                      '0%, 100%': { transform: 'translateY(0px) scale(1)', filter: 'drop-shadow(0 0 15px rgba(99, 102, 241, 0.4))' },
                      '50%': { transform: 'translateY(-8px) scale(1.03)', filter: 'drop-shadow(0 0 25px rgba(168, 85, 247, 0.7))' },
                    },
                    animation: 'premiumIntro 1.2s cubic-bezier(0.34, 1.56, 0.64, 1) forwards, floatAndGlow 4s ease-in-out infinite 1.2s'
                  }}
                />
              ) : (
                <PeopleIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              )}
              
              <Typography 
                variant="h4" 
                component="h1" 
                gutterBottom
                sx={{ 
                  fontWeight: 900, 
                  fontFamily: '"Outfit", "Inter", sans-serif', 
                  letterSpacing: '0.5px',
                  background: 'linear-gradient(90deg, #38bdf8 0%, #c084fc 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1
                }}
              >
                {companySettings.company_name}
              </Typography>
              
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontWeight: 500, fontSize: '0.875rem' }}>
                Accede a tu cuenta para gestionar tu servicio de internet
              </Typography>
            </Box>

            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3, 
                  borderRadius: '12px',
                  bgcolor: 'rgba(239, 68, 68, 0.1)',
                  color: '#f87171',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  '& .MuiAlert-icon': { color: '#f87171' }
                }}
              >
                {error}
              </Alert>
            )}

            {success && (
              <Alert 
                severity="success" 
                sx={{ 
                  mb: 3, 
                  borderRadius: '12px',
                  bgcolor: 'rgba(34, 197, 94, 0.1)',
                  color: '#4ade80',
                  border: '1px solid rgba(34, 197, 94, 0.2)',
                  '& .MuiAlert-icon': { color: '#4ade80' }
                }}
              >
                {success}
              </Alert>
            )}

            {view === 'login' ? (
              <Box component="form" onSubmit={handleSubmit} noValidate>
                <TextField
                  fullWidth
                  label="Usuario o Correo"
                  name="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  margin="normal"
                  required
                  disabled={loading}
                  autoFocus
                  InputLabelProps={{
                    sx: { color: 'rgba(255, 255, 255, 0.4)', '&.Mui-focused': { color: '#38bdf8' } }
                  }}
                  InputProps={{
                    sx: {
                      color: '#ffffff',
                      bgcolor: 'rgba(15, 23, 42, 0.45)',
                      borderRadius: '12px',
                      transition: 'all 0.3s ease',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.08)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#38bdf8',
                        boxShadow: '0 0 12px rgba(56, 189, 248, 0.15)'
                      }
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label="Contraseña"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin="normal"
                  required
                  disabled={loading}
                  InputLabelProps={{
                    sx: { color: 'rgba(255, 255, 255, 0.4)', '&.Mui-focused': { color: '#c084fc' } }
                  }}
                  InputProps={{
                    sx: {
                      color: '#ffffff',
                      bgcolor: 'rgba(15, 23, 42, 0.45)',
                      borderRadius: '12px',
                      transition: 'all 0.3s ease',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.08)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#c084fc',
                        boxShadow: '0 0 12px rgba(192, 132, 252, 0.15)'
                      }
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleTogglePassword}
                          edge="end"
                          sx={{ 
                            color: 'rgba(255, 255, 255, 0.45)',
                            '&:hover': { color: '#c084fc' }
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    mt: 4,
                    py: 1.5,
                    borderRadius: '12px',
                    background: 'linear-gradient(95deg, #38bdf8 0%, #c084fc 100%)',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    boxShadow: '0 4px 20px rgba(192, 132, 252, 0.3)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: 'linear-gradient(95deg, #0ea5e9 0%, #a855f7 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 24px rgba(192, 132, 252, 0.45)',
                    },
                    '&:active': {
                      transform: 'translateY(1px)',
                    },
                    '&.Mui-disabled': {
                      background: 'rgba(255, 255, 255, 0.12)',
                      color: 'rgba(255, 255, 255, 0.3)'
                    }
                  }}
                >
                  {loading ? <CircularProgress size={24} sx={{ color: '#ffffff' }} /> : 'Iniciar Sesión'}
                </Button>
              </Box>
            ) : (
              <Box component="form" onSubmit={handleForgotPasswordSubmit} noValidate>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 3, lineHeight: 1.6, textAlign: 'center' }}>
                  Ingresa tu correo electrónico registrado y te enviaremos un enlace para restablecer tu contraseña.
                </Typography>
                
                <TextField
                  fullWidth
                  label="Correo Electrónico"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  margin="normal"
                  required
                  disabled={loading}
                  autoFocus
                  InputLabelProps={{
                    sx: { color: 'rgba(255, 255, 255, 0.4)', '&.Mui-focused': { color: '#38bdf8' } }
                  }}
                  InputProps={{
                    sx: {
                      color: '#ffffff',
                      bgcolor: 'rgba(15, 23, 42, 0.45)',
                      borderRadius: '12px',
                      transition: 'all 0.3s ease',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.08)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#38bdf8',
                        boxShadow: '0 0 12px rgba(56, 189, 248, 0.15)'
                      }
                    }
                  }}
                />
                
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    mt: 4,
                    py: 1.5,
                    borderRadius: '12px',
                    background: 'linear-gradient(95deg, #38bdf8 0%, #c084fc 100%)',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    boxShadow: '0 4px 20px rgba(192, 132, 252, 0.3)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: 'linear-gradient(95deg, #0ea5e9 0%, #a855f7 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 24px rgba(192, 132, 252, 0.45)',
                    },
                    '&:active': {
                      transform: 'translateY(1px)',
                    },
                    '&.Mui-disabled': {
                      background: 'rgba(255, 255, 255, 0.12)',
                      color: 'rgba(255, 255, 255, 0.3)'
                    }
                  }}
                >
                  {loading ? <CircularProgress size={24} sx={{ color: '#ffffff' }} /> : 'Enviar Enlace'}
                </Button>
              </Box>
            )}

            <Box sx={{ mt: 4, textAlign: 'center' }}>
              {view === 'login' ? (
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.45)' }}>
                  ¿Olvidaste tu contraseña?{' '}
                  <Box
                    component="span"
                    onClick={() => toggleView('forgot')}
                    sx={{
                      color: '#38bdf8',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'color 0.2s',
                      '&:hover': { color: '#c084fc' }
                    }}
                  >
                    Recuperar ahora
                  </Box>
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.45)' }}>
                  ¿Recordaste tu contraseña?{' '}
                  <Box
                    component="span"
                    onClick={() => toggleView('login')}
                    sx={{
                      color: '#38bdf8',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'color 0.2s',
                      '&:hover': { color: '#c084fc' }
                    }}
                  >
                    Volver al login
                  </Box>
                </Typography>
              )}
            </Box>

            <Box sx={{ mt: 3, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <Button
                variant="text"
                size="small"
                onClick={() => setTutorialOpen(true)}
                sx={{
                  color: 'rgba(255, 255, 255, 0.45)',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    color: '#38bdf8',
                    background: 'transparent'
                  }
                }}
              >
                📖 ¿Cómo usar el portal? Ver Guía
              </Button>

              <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', mt: 1.5 }}>
                <Typography
                  variant="caption"
                  onClick={() => setTermsOpen(true)}
                  sx={{
                    color: '#e2e8f0',
                    cursor: 'pointer',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    textDecoration: 'underline',
                    textUnderlineOffset: '3px',
                    '&:hover': { color: '#38bdf8' }
                  }}
                >
                  📜 Términos y Condiciones
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.3)' }}>|</Typography>
                <Box
                  onClick={() => setComplaintsOpen(true)}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    bgcolor: 'rgba(245, 158, 11, 0.15)',
                    color: '#fbbf24',
                    border: '1px solid rgba(245, 158, 11, 0.4)',
                    px: 1.2,
                    py: 0.3,
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'rgba(245, 158, 11, 0.25)',
                      color: '#fef08a',
                      borderColor: '#fef08a'
                    }
                  }}
                >
                  📘 Libro de Reclamaciones
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <HelpTutorialDialog open={tutorialOpen} onClose={() => setTutorialOpen(false)} />
        <TermsModal open={termsOpen} onClose={() => setTermsOpen(false)} companyName={companySettings.company_name} />
        <ComplaintsBookModal open={complaintsOpen} onClose={() => setComplaintsOpen(false)} companyName={companySettings.company_name} />
      </Container>
    </Box>
  );
};

export default ClientLogin;